import React, { useEffect, useState } from 'react';
import './SaveToast.css';

export type SaveState = 'saving' | 'saved' | 'error' | null;

/**
 * Save-state toast, bottom-right of the viewport, in the study's voice and outside the
 * inbox (advisor F21). "Saved" fades after 1.5 s; "Could not save — retrying" stays until
 * a save succeeds.
 */
const SaveToast: React.FC<{ state: SaveState }> = ({ state }) => {
  const [visible, setVisible] = useState<SaveState>(null);
  useEffect(() => {
    if (!state) { setVisible(null); return; }
    setVisible(state);
    if (state === 'saved') { const t = setTimeout(() => setVisible(null), 1500); return () => clearTimeout(t); }
  }, [state]);
  if (!visible) return null;
  const text = visible === 'saving' ? 'Saving…' : visible === 'saved' ? 'Saved' : 'Could not save — retrying';
  return <div className={`save-toast ${visible}`} role="status" aria-live="polite" data-testid="save-toast">{text}</div>;
};

export default SaveToast;

/** Hook: turns the outcome of persist() calls into a SaveState. */
export function useSaveState(): [SaveState, (p: Promise<boolean>) => Promise<boolean>] {
  const [state, setState] = useState<SaveState>(null);
  const track = async (p: Promise<boolean>) => {
    setState(s => (s === 'error' ? 'error' : 'saving'));
    const ok = await p;
    setState(ok ? 'saved' : 'error');
    return ok;
  };
  return [state, track];
}
